import { ProductCherryPickHelper } from '@athena-lib/ProductWizard/model/ProductCherryPickHelper';
import { productWizardConsole } from '@athena-lib/ProductWizard/util/productWizardLog';
import { ProductItem } from '../model/ProductItem';
import { ProductAttrComputer } from '../helper/ProductAttrComputer';
import { ProductProcessorsCollection } from '../processor/ProductProcessorsCollection';
import type { ProductItemInterface } from '../model/ProductItemInterface';
import type { ProductAttrMapInterface } from '../model/attr/ProductAttrMapInterface';
import type { ProductItemAttributesInterface } from '../model/ProductItemAttributesInterface';
import { ProductProcessorsEnum } from '../processor/ProductProcessorsEnum';
import { ProductAttrConditionEvaluator } from '../helper/ProductAttrConditionEvaluator';
import { FixBooleanAttrValueProcessor } from './processor/FixBooleanAttrValueProcessor';

export class ProductContext {
  public productItem: ProductItem;
  public attrComputer: ProductAttrComputer;
  public attrEvaluator: ProductAttrConditionEvaluator;
  public cherryPicks: ProductCherryPickHelper;
  public processors: ProductProcessorsCollection = new ProductProcessorsCollection();

  constructor() {
    this.productItem = new ProductItem();
    this.attrEvaluator = new ProductAttrConditionEvaluator();
    this.attrComputer = new ProductAttrComputer(this.attrEvaluator);
    this.cherryPicks = new ProductCherryPickHelper(this.attrComputer, {});
    this.processors.registerProcessor(
      ProductProcessorsEnum.FIX_ATTR_BOOL_VALUES,
      new FixBooleanAttrValueProcessor(),
    );
  }

  public reset(
    productItem: ProductItemInterface,
    attributesMap: ProductAttrMapInterface,
    keepCherryPicks: boolean = false,
  ): void {
    this.productItem.productName = productItem.productName;
    this.productItem.productFamilyName = productItem.productFamilyName;
    this.productItem.units = productItem.units;
    this.productItem.attributes = productItem.attributes;
    this.productItem.sku = productItem.sku;

    this.attrComputer.reset(attributesMap);
    this.attrEvaluator.reset(attributesMap);
    if (!keepCherryPicks) {
      this.cherryPicks.reset();
    }
  }

  public invokeProcessors(): void {
    productWizardConsole.groupCollapsed('ProductContext::invokeProcessors()');
    this.processors.invokeProcessors(this.productItem, this.attrComputer);
    productWizardConsole.groupEnd();
  }

  public setCherryPicks(
    attributes: ProductItemAttributesInterface,
    setAlsoAsPreferred: boolean = false,
  ): void {
    productWizardConsole.log(
      'ProductContext::setCherryPicks()',
      'preferred',
      setAlsoAsPreferred,
      JSON.stringify(attributes),
    );
    this.cherryPicks.attributes = attributes;

    if (setAlsoAsPreferred) {
      /**
       * A part from setting cherry picks this will also tell the ProductAttrComputer to include
       * the attribute values you desire in the filters (if those values are not constrained).
       *
       * This will help when QT creates orders with material / laminate combinations that aren't visible to the customers (filtered out)
       * and prevent editor to reset the material / laminate combination to only what's visible to customer.
       */
      this.attrComputer.attributesPreferred = JSON.parse(
        JSON.stringify(attributes),
      ) as ProductItemAttributesInterface;
    }
  }

  public resetPreferredValues(): void {
    this.attrComputer.attributesPreferred = {};
  }
}
